import { message } from 'antd';
import { API_URL } from '../apiUrl';



const isCoord = (n) => typeof n === 'number' && !Number.isNaN(n);

const hasCityLocation = (avatar) => {
  const loc = avatar?.aggr && !Array.isArray(avatar.aggr) ? avatar.aggr.city?.location : null;
  return Boolean(loc && isCoord(loc.lat) && isCoord(loc.lon));
};

const applyCityToAvatar = (avatar, city) => {
  if (!avatar || !city) return avatar;
  const prevAggr = avatar.aggr && !Array.isArray(avatar.aggr) ? avatar.aggr : {};
  return {
    ...avatar,
    aggr: {
      ...prevAggr,
      city: {
        ...(prevAggr.city || {}),
        ...city,
      },
    },
  };
};

const cityCache = new Map();
const cityInflight = new Map();

export const getCachedCity = (cityId) => {
  if (!cityId && cityId !== 0) return null;
  return cityCache.get(String(cityId)) || null;
};

/** Cached geo/city/:id fetch shared by penta astro/jyotish hydration. */
export const getCityById = (cityId) => {
  if (!cityId && cityId !== 0) return Promise.resolve(null);
  const key = String(cityId);
  if (cityCache.has(key)) return Promise.resolve(cityCache.get(key));
  if (cityInflight.has(key)) return cityInflight.get(key);

  const pending = fetch(`${API_URL}/api/geo/city/${cityId}`)
    .then((response) => {
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      return response.json();
    })
    .then((dat) => {
      cityCache.set(key, dat);
      cityInflight.delete(key);
      return dat;
    })
    .catch((error) => {
      cityInflight.delete(key);
      console.error('Error fetching city:', error);
      return null;
    });

  cityInflight.set(key, pending);
  return pending;
};

/**
 * Ensure avatar has aggr.city.location for astrology.
 * Prefer existing city, else fetch by city_id (penta members often have empty aggr).
 */
export const ensureAvatarCity = async (avatar) => {
  if (!avatar || hasCityLocation(avatar)) return avatar;
  const cityId = avatar.city_id || avatar.aggr?.city?.id;
  if (!cityId) return avatar;
  const city = await getCityById(cityId);
  if (!city?.location || !isCoord(city.location.lat) || !isCoord(city.location.lon)) {
    return avatar;
  }
  return applyCityToAvatar(avatar, city);
};

// Some endpoints (geo search, penta members) return avatars with an empty `aggr`,
// so astro/jyotish can't be computed (both need `avatar.aggr.city.location`). The
// person's coordinates are available on `user.location`, so backfill them here.
export const withCityLocation = (persona) => {
  const avatar = persona?.avatar;
  const loc = persona?.user?.location;
  if (!avatar || !loc || !isCoord(loc.lat) || !isCoord(loc.lon)) return persona;
  if (hasCityLocation(avatar)) {
    return persona;
  }
  const prevAggr = avatar.aggr && !Array.isArray(avatar.aggr) ? avatar.aggr : {};
  return {
    ...persona,
    avatar: {
      ...avatar,
      aggr: {
        ...prevAggr,
        city: {
          ...(prevAggr.city || {}),
          location: { lat: loc.lat, lon: loc.lon },
        },
      },
    },
  };
};

export const getGeoLocation = () => {
  return new Promise((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(resolve, reject);
    } else {
      reject(new Error("Geolocation is not supported by this browser."));
    }
  });
};

export const showGeoPosition = (position) => {
  const latitude = position.coords.latitude;
  const longitude = position.coords.longitude;
  console.log(`Latitude: ${latitude}, Longitude: ${longitude}`);

  // You can now use the latitude and longitude for your app's functionality
};

const showGeoError = (error) => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      alert("User denied the request for Geolocation.");
      break;
    case error.POSITION_UNAVAILABLE:
      alert("Location information is unavailable.");
      break;
    case error.TIMEOUT:
      alert("The request to get user location timed out.");
      break;
    case error.UNKNOWN_ERROR:
      alert("An unknown error occurred.");
      break;
  }
};



export const fetchNearestCities = async (latitude, longitude) => {
  try {
    const response = await fetch(`${API_URL}/api/geo/close?lat=${latitude}&lon=${longitude}`);
    if (!response.ok) {
      throw new Error('Failed to fetch city data');
    }
    const data = await response.json();
    return data; // Return the city data directly
  } catch (error) {
    message.error("Failed to fetch city data");
    console.error("Error fetching city data:", error);
    return []; // Return an empty array in case of error
  }
};

export const checkProvince = (input) => {
  if (!input) return '';

  const firstChar = input.charAt(0);

  if (/^\d/.test(firstChar)) {
    return '';
  } else if (/^[a-zA-Z]/.test(firstChar)) {
    return input;
  } else {
    return '';
  }
};




export const handleCitySearch = async (selectedToken = '', callback = () => void (0)) => {
  const url = `${API_URL}/api/geo/cities?search=${selectedToken}`;
  // const url = `https://hd.cafe/api/geo/cities?search=${selectedToken}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
    });
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }
    const data = await response.json();
    console.log('Success:', data);
    callback(data)
  } catch (error) {
    console.error('Error:', error.message);
  }
};

export const handleCitySearchHD = async (cityName = '') => {
  const url = `https://hd.cafe/api/geo/cities?search=${cityName}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    const data = await response.json();
    console.log('Success:', data);
    return data;  // Return the data directly

  } catch (error) {
    console.error('Error:', error.message);
    return [];  // Return an empty array or handle the error as needed
  }
};



export const fetchGeoUsers = async (
  { lat = 0, lon = 0, distance = 20100, limit = 2000 } = {},
  handleData = () => void (0),
  handleError = () => void (0)
) => {
  const url = `${API_URL}/api/search/users?lat=${lat}&lon=${lon}&distance=${distance}&limit=${limit}`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }
    const data = await response.json();
    handleData(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error('Error fetching geo users:', error.message);
    handleError(error);
  }
};



export const _getCity = async (cityId, handleData = () => void (0)) => {
  const dat = await getCityById(cityId);
  if (dat) handleData(dat);
}



export const _updateMyCity = async (newCityId = '', callback = () => void (0)) => {
  const url = `${API_URL}/api/me/update`;

  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        // Include any other headers you need, such as authorization
      },
      // If you need to send a body with the POST request, include it here
      body: JSON.stringify({ city_id: newCityId }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status}`);
    }

    const data = await response.json();
    console.log('Success:', data);
    callback && callback(data)
    // Handle success, e.g., showing a success message
  } catch (error) {
    console.error('Error:', error.message);
    // Handle error, e.g., showing an error message
  }
};